// Labels and formatters for Customer Details. The wording matches the teacher
// portal's own screens (mindcircuit-app/src/lib/resetLabels.ts), so support and
// the teacher use the same words for the same thing.

export const STATE_LABELS = {
  calm: "Restless",
  focus: "Attention Scattered",
  clarity: "Stuck",
  energy: "Low Energy",
  comfort: "Body Tension",
};

export const STATE_COLORS = {
  calm: "#2451c4",
  focus: "#d97f2b",
  clarity: "#5e3fc4",
  energy: "#e85d2b",
  comfort: "#1f8f52",
};

export const CONTEXT_LABELS = {
  TRANSITION: "Transition time",
  AFTER_LUNCH: "After lunch",
  BEFORE_TEST: "Before a test",
  MORNING_MEETING: "Morning meeting",
  RETURN_FROM_RECESS: "Return from recess",
  UNSPECIFIED: "Not recorded",
};

export const RETURN_TO_TASK_ORDER = [
  "UNDER_1_MIN",
  "ONE_TO_TWO_MIN",
  "THREE_TO_FIVE_MIN",
  "FIVE_PLUS_MIN",
];

export const RETURN_TO_TASK_LABELS = {
  UNDER_1_MIN: "Under 1 min",
  ONE_TO_TWO_MIN: "1–2 min",
  THREE_TO_FIVE_MIN: "3–5 min",
  FIVE_PLUS_MIN: "5+ min",
};

export const ROLE_LABELS = {
  Educator: "Educator",
  Counselor: "Counselor",
  Student: "Student",
  SchoolAdministrator: "School administrator",
  DistrictAdministrator: "District administrator",
  SupportStaff: "Support staff",
};

export const SIGN_IN_LABELS = {
  PASSWORD: "Email & password",
  GOOGLE: "Google",
  CLEVER: "Clever",
  APPLE: "Apple",
  FACEBOOK: "Facebook",
  SNAPCHAT: "Snapchat",
};

export const GRADE_BAND_LABELS = {
  EARLY_LEARNING: "Early learning",
  ELEMENTARY: "Elementary",
  MIDDLE: "Middle school",
  HIGH: "High school",
};

const DAY_MS = 24 * 60 * 60 * 1000;

export const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

/**
 * Date and time in the given IANA zone — the teacher's own, on Customer
 * Details, so a reset reads as the time on their classroom clock rather than
 * the admin's. Falls back to the browser's zone for an unknown one.
 */
export const formatDateTime = (iso, timeZone) => {
  if (!iso) return "—";
  const options = {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  };
  try {
    return new Date(iso).toLocaleString(
      "en-US",
      timeZone ? { ...options, timeZone } : options,
    );
  } catch {
    return new Date(iso).toLocaleString("en-US", options);
  }
};

/** "3 days ago", "today", or a date once it is more than a month back. */
export const formatRelative = (iso, now = Date.now()) => {
  if (!iso) return "Never";
  const days = Math.floor((now - new Date(iso).getTime()) / DAY_MS);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 31) return `${days} days ago`;
  return formatDate(iso);
};

/** Minutes as the largest unit that reads naturally: 45 min, 3.5 h, 2.1 days. */
export const formatMinutes = (minutes) => {
  if (minutes == null) return "—";
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 24) return `${Number(hours.toFixed(1))} h`;
  return `${Number((hours / 24).toFixed(1))} days`;
};

// From integer cents, never through a float: 9.99 has no exact binary form.
export const formatMoney = (cents, currency = "USD") => {
  if (cents == null) return "—";
  const symbol = currency === "USD" ? "$" : `${currency} `;
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}${symbol}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
};

/**
 * One line that answers "does this person have access, and why".
 * `tone` picks the pill colour: good, warn, bad or neutral.
 */
export const describeAccess = (access, now = Date.now()) => {
  if (!access) return { label: "—", detail: "", tone: "neutral" };
  const until = access.expiresAt ? new Date(access.expiresAt).getTime() : null;
  const daysLeft =
    until != null ? Math.max(0, Math.ceil((until - now) / DAY_MS)) : null;

  let result;
  switch (access.source) {
    case "TRIAL":
      result = {
        label: "Free trial",
        detail:
          daysLeft != null
            ? `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left · ends ${formatDate(access.expiresAt)}`
            : "",
        tone: daysLeft != null && daysLeft <= 3 ? "warn" : "good",
      };
      break;
    case "INDIVIDUAL":
      result = {
        label: "Premium · individual plan",
        detail: until ? `Paid through ${formatDate(access.expiresAt)}` : "",
        tone: "good",
      };
      break;
    case "SCHOOL":
      result = {
        label: "Premium · school seat",
        detail: until ? `Licence runs to ${formatDate(access.expiresAt)}` : "",
        tone: "good",
      };
      break;
    case "GRANDFATHERED":
      result = {
        label: "Grandfathered",
        detail: "Permanent access from before the paywall",
        tone: "good",
      };
      break;
    default:
      result = access.trialEndsAt
        ? {
            label: "No active plan",
            detail: `Trial ended ${formatDate(access.trialEndsAt)}`,
            tone: "bad",
          }
        : {
            label: "No plan yet",
            detail: "Has not started a trial",
            tone: "neutral",
          };
  }
  // While the paywall is off nothing is withheld, whatever the status says.
  if (access.enforced === false)
    result.detail = [result.detail, "paywall not enforced"]
      .filter(Boolean)
      .join(" · ");
  return result;
};
